import { IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText } from '@mui/material';
import { Delete, Folder, Description } from '@mui/icons-material';
import { DisplayItem } from '../types/types';
import { PathPickerType } from '../types/enums';
import { fetchItems } from '../actions';
import { equals, isEmpty, symmetricDifference } from 'ramda';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { createPortal } from 'react-dom';

interface ItemListProps {
	selected: string[];
	onDeleteBtnClick: Function;
	isMandatory: boolean;
	disabled: boolean;
	hidden: string[];
	pckg_mode: boolean;
	bpm_context: string;
	changeOrder?: Function;
}

interface ItemListState {
	selectedItems: DisplayItem[];
}

class DisplayList extends window.React.PureComponent<ItemListProps, ItemListState> {
	constructor(props: ItemListProps) {
		super(props);
		this.state = {
			selectedItems: [],
		};
	}

	componentDidMount() {
		const { selected } = this.props;
		this.setSelectedItems(selected || []);
	}

	componentDidUpdate(prevProps: ItemListProps) {
		const { selected } = this.props;

		if (!equals(prevProps.selected, selected)) {
			const isContentSame = isEmpty(symmetricDifference(prevProps.selected || [], selected || []));

			if (!isContentSame) {
				this.setSelectedItems(selected || []);
			} else {

				const reorderedItems = (selected || [])
					.map(ref => this.state.selectedItems.find(item => item.nodeRef === ref))
					.filter((item): item is DisplayItem => !!item);

				this.setState({
					selectedItems: reorderedItems,
				});
			}
		}
	}

	setSelectedItems = async (nodeRefs: string[]) => {
		const { hidden } = this.props;

		const selectedItems: DisplayItem[] = await fetchItems(nodeRefs, hidden);

		this.setState({
			selectedItems,
		});
	};

	forwardToItem = (item: DisplayItem) => {
		const { type, nodeRef, path, name } = item;

		if (type === PathPickerType.FILE) {
			window.location.href = `${window.appContext}/page/document-details?nodeRef=${nodeRef}`;
		} else if (path) {
			window.location.href = `${window.appContext}/page/site/${path
				.map(item2 => (item2.name === 'documentLibrary' ? 'documentlibrary#/' : item2.name))
				.join('/')}/${name === 'documentLibrary' ? 'documentlibrary#' : name}/?page=1`;
		}
	};

	getHref = (item: DisplayItem) => {
		const { type, nodeRef, path, name } = item;

		if (type === PathPickerType.FILE) {
			return `${window.appContext}/page/document-details?nodeRef=${nodeRef}`;
		} else if (path) {
			return `${window.appContext}/page/site/${path
				.map(item2 => (item2.name === 'documentLibrary' ? 'documentlibrary#/' : item2.name))
				.join('/')}/${name === 'documentLibrary' ? 'documentlibrary#' : name}/?page=1`;
		}
	};

	onDelete = (nodeRef: string) => {
		const { onDeleteBtnClick } = this.props;

		onDeleteBtnClick(nodeRef);
	};

	render() {
		const { isMandatory, disabled, pckg_mode, bpm_context, changeOrder } = this.props;

		const { selectedItems } = this.state;

		return (
			<DragDropContext onDragEnd={changeOrder}>
				<Droppable droppableId={'documentsList'}>
					{(provided: any) => (
						<List ref={provided.innerRef} {...provided.droppableProps}>
							{selectedItems?.map((item: DisplayItem, _index: number, array: DisplayItem[]) => {
								const showDeleteBtn = !(isMandatory && array.length === 1) && !disabled && item.deletable;
								const isPckg_mode = pckg_mode && item.nodeRef !== bpm_context;

								const { type, nodeRef, name, path } = item;

								return (
									<Draggable
										draggableId={`draggableRule${_index}`}
										index={_index}
										isDragDisabled={!changeOrder}
									>
										{(provided: any, snapshot: any) => {
											const listItem = (
												<ListItem
												{...provided.draggableProps}
												{...(changeOrder ? provided.dragHandleProps : {})}
												ref={provided.innerRef}
												key={nodeRef}
												/*onClick={() => this.forwardToItem(item)}*/
												className="PathPicker-Field"
												secondaryAction={
													(showDeleteBtn && (pckg_mode !== undefined ? isPckg_mode : true)) && (
													<IconButton onClick={() => this.onDelete(nodeRef)} edge="end" aria-label="delete">
														<Delete />
													</IconButton>
													)
												}
											>
												<ListItemIcon>{type === PathPickerType.FOLDER ? <Folder /> : <Description />}</ListItemIcon>
												<ListItemText
													style={{
														paddingRight: showDeleteBtn ? '60px' : 0,
														wordBreak: 'break-all',
													}}
													primary={
														<a href={this.getHref(item)} target="_blank">
															{name}
														</a>
													}
													secondary={
														path
															? path.every(pathEl => pathEl.name === '')
																? ''
																: path.map(pathEl => pathEl.name || pathEl).join('/')
															: null
													}
												/>
											</ListItem>
											)
											if (snapshot.isDragging) {
												return createPortal(listItem, document.body);
											}
											return listItem;
										}}
									</Draggable>
								);
							})}
							{provided.placeholder}
						</List>
					)}
				</Droppable>
			</DragDropContext>
		);
	}
}

export default DisplayList;
